import { Navigate, useLocation } from 'react-router-dom';
import { useEffect, useRef } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useBillingStatus } from '@/hooks/useBillingStatus';
import { useUserRoles } from '@/hooks/useUserRoles';
import { useIsSuperAdmin } from '@/hooks/useIsSuperAdmin';
import { toast } from 'sonner';
import brandLogoHorizontal from '@/assets/brand/nivela-logo-horizontal.png';

interface AdminProtectedRouteProps {
  children: React.ReactNode;
}

/**
 * Gate de rota para TODA URL `/admin/*`.
 *
 * Camadas (defense in depth):
 *  1. Auth válida (sessão Supabase ativa)
 *  2. Perfil aprovado (`userStatus === 'approved'`)
 *  3. Não suspenso por inadimplência (exceto super_admin/dev em /admin/billing)
 *  4. Role `super_admin` confirmada via RPC `is_super_admin` (SECURITY DEFINER)
 *
 * As páginas admin NÃO devem depender de `AdminLayout` para autorização — esse
 * componente é a única fonte de verdade no nível da rota.
 */
export function AdminProtectedRoute({ children }: AdminProtectedRouteProps) {
  const { user, loading: authLoading, userStatus } = useAuth();
  const { status: billingStatus, loading: billingLoading } = useBillingStatus();
  const { isAdmin, isDeveloper, loading: rolesLoading } = useUserRoles();
  const { isSuperAdmin, loading: superAdminLoading } = useIsSuperAdmin();
  const location = useLocation();
  const notifiedRef = useRef(false);

  const stillLoading = authLoading || billingLoading || superAdminLoading || rolesLoading;
  const hasAdminAccess = isSuperAdmin || isAdmin || isDeveloper;

  useEffect(() => {
    if (stillLoading) return;
    if (user && userStatus === 'approved' && !hasAdminAccess && !notifiedRef.current) {
      notifiedRef.current = true;
      toast.error('Acesso restrito ao administrador.');
    }
  }, [stillLoading, user, userStatus, hasAdminAccess]);

  if (stillLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <img src={brandLogoHorizontal} alt="Nível A" className="h-10 w-auto animate-pulse" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  if (userStatus && userStatus !== 'approved') {
    return <Navigate to="/auth" replace />;
  }

  // Suspensão: super_admin/dev pode entrar só em /admin/billing para regularizar.
  if (billingStatus === 'suspended') {
    const isOwnerOrDev = isSuperAdmin || isDeveloper;
    const onAdminBilling = location.pathname.startsWith('/admin/billing');
    if (!(isOwnerOrDev && onAdminBilling)) {
      return <Navigate to="/auth" replace />;
    }
  }

  if (!hasAdminAccess) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}
